import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { 
  FiX, FiLink, FiMail, FiUsers, FiLock, 
  FiCheck, FiCopy, FiSlack, FiMessageSquare,
  FiShield, FiCalendar, FiGlobe
} = FiIcons;

const ReportShareModal = ({ isOpen, onClose, reportTitle }) => {
  const [activeTab, setActiveTab] = useState('people');
  const [copied, setCopied] = useState(false);
  const [accessLevel, setAccessLevel] = useState('viewer');
  const [emails, setEmails] = useState('');

  const shareLink = `https://b360.cansulta.com/share/rpt-${Math.random().toString(36).substr(2, 9)}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-gray-200"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Share Report</h2>
            <p className="text-xs text-gray-500 truncate max-w-[300px]">{reportTitle}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-400">
            <SafeIcon icon={FiX} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          {[
            { id: 'people', label: 'People', icon: FiUsers },
            { id: 'link', label: 'Public Link', icon: FiGlobe },
            { id: 'apps', label: 'Integrations', icon: FiSlack }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-bold flex items-center justify-center gap-2 transition-all border-b-2 ${
                activeTab === tab.id 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <SafeIcon icon={tab.icon} className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-6 min-h-[320px]">
          <AnimatePresence mode="wait">
            {activeTab === 'people' && (
              <motion.div 
                key="people"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">Invite by Email</label>
                  <div className="flex gap-2">
                    <input 
                      type="text" 
                      placeholder="Enter names or email addresses..."
                      className="flex-1 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 transition-all"
                      value={emails}
                      onChange={(e) => setEmails(e.target.value)}
                    />
                    <select 
                      className="bg-gray-50 border border-gray-200 rounded-xl px-2 text-xs font-bold text-gray-600"
                      value={accessLevel}
                      onChange={(e) => setAccessLevel(e.target.value)}
                    >
                      <option value="viewer">Viewer</option>
                      <option value="commenter">Commenter</option>
                      <option value="editor">Editor</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">People with access</p>
                  <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-xl transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">JD</div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">John Doe (You)</p>
                        <p className="text-xs text-gray-500">Owner</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-xl transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-bold">AL</div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">Alex Lee</p>
                        <p className="text-xs text-gray-500">alex@company.com</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 uppercase">Viewer</span>
                  </div>
                </div>
              </motion.div>
            )}

            {activeTab === 'link' && (
              <motion.div 
                key="link"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-6"
              >
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                  <SafeIcon icon={FiShield} className="text-blue-600 mt-1" />
                  <div>
                    <h4 className="text-sm font-bold text-blue-900">Public Access is Disabled</h4>
                    <p className="text-xs text-blue-700 leading-relaxed">Turn on public link sharing to allow anyone with the link to view this report without an account.</p>
                  </div>
                </div>

                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <SafeIcon icon={FiCalendar} className="text-gray-400" />
                    <div>
                      <p className="text-sm font-bold text-gray-900">Set Expiration</p>
                      <p className="text-xs text-gray-500">Link will expire in 30 days</p>
                    </div>
                  </div>
                  <input type="checkbox" className="w-4 h-4 rounded text-blue-600" />
                </div>

                <div className="relative">
                  <input 
                    readOnly 
                    value={shareLink}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-4 pr-24 py-3 text-xs text-gray-500 font-mono"
                  />
                  <button 
                    onClick={handleCopyLink}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 px-4 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm ${
                      copied ? 'bg-green-500 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                </div>
              </motion.div>
            )}

            {activeTab === 'apps' && (
              <motion.div 
                key="apps"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                className="space-y-4"
              >
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Send to Channels</p>
                <div className="grid grid-cols-1 gap-3">
                  <button className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50/30 transition-all group">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#4A154B] flex items-center justify-center text-white">
                        <SafeIcon icon={FiSlack} className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-900">Share to Slack</p>
                        <p className="text-xs text-gray-500">Post directly to #strategy-team</p>
                      </div>
                    </div>
                    <SafeIcon icon={FiCheck} className="text-green-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                  <button className="flex items-center justify-between p-4 border border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50/30 transition-all group">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#6264A7] flex items-center justify-center text-white">
                        <SafeIcon icon={FiMessageSquare} className="w-6 h-6" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-900">Microsoft Teams</p>
                        <p className="text-xs text-gray-500">Share with Leadership Channel</p>
                      </div>
                    </div>
                    <SafeIcon icon={FiCheck} className="text-gray-300" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <SafeIcon icon={FiLock} className="w-3 h-3" />
            <span>Encrypted Sharing</span>
          </div>
          <button 
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700 shadow-md transition-all"
          >
            Invite Members
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ReportShareModal;